import React from "react";
import {
  ShieldCheck,
  Heart,
  Handshake,
  MapPin,
  Globe,
  ShieldCheckIcon,
  Compass,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// ─── Data ────────────────────────────────────────────────────────────────────

const stats = [
  { value: "30+", label: "Suppliers" },
  { value: "180+", label: "Cities" },
  { value: "< 4s", label: "Avg. search time" },
  { value: "< 2 min", label: "Support response" },
];

const principles = [
  {
    icon: <ShieldCheck size={24} className="text-primary" />,
    title: "Transparent pricing",
    desc: "Every fee, deposit, and add-on shown upfront — no surprises at the counter.",
  },
  {
    icon: <Heart size={24} className="text-primary" />,
    title: "Travelers first",
    desc: "We're real renters too. Every decision is judged by what makes your trip better.",
  },
  {
    icon: <Handshake size={24} className="text-primary" />,
    title: "Partners we trust",
    desc: "We vet every supplier on fleet quality, service, and clear cancellation rules.",
  },
  {
    icon: <Sparkles size={24} className="text-primary" />,
    title: "Quietly relentless",
    desc: "Small, focused team obsessed with shaving seconds off every booking step.",
  },
];

const steps = [
  {
    step: "STEP 1",
    icon: <MapPin size={28} className="text-primary" />,
    title: "Tell us where & when",
    desc: "One search across every partner — instantly.",
  },
  {
    step: "STEP 2",
    icon: <Globe size={28} className="text-primary" />,
    title: "Compare clearly",
    desc: "Side-by-side prices, inclusions, and cancellation rules.",
  },
  {
    step: "STEP 3",
    icon: <ShieldCheckIcon size={28} className="text-primary" />,
    title: "Book with confidence",
    desc: "Free cancellation on most cars, real humans on call.",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function AboutUs() {
  const navigate = useNavigate();
  return (
    <main className="w-full font-sans">
      {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
      <section
        className="relative w-full flex items-center overflow-hidden mt-16"
        style={{
          /* Pure green gradient: near-black green left → medium forest green right */
          background:
            "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
          minHeight: "420px",
        }}
      >
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-16 pb-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 rounded-full px-3 py-1 mb-3">
            <Compass size={12} className="text-white" />
            <span className="text-white text-xs font-semibold font-body tracking-widest uppercase">
              Our Story
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black font-heading text-white leading-[1.08] max-w-2xl mb-3 tracking-normal">
            Car rental, without the runaround.
          </h1>

          {/* Sub-text */}
          <p className="font-body text-white/80 text-lg md:text-lg max-w-2xl leading-relaxed mb-7">
            We built Rent Gurus because booking a car shouldn't feel like
            reading fine print. One search, every major supplier, honest prices
            — so you spend less time comparing and more time driving.
          </p>

          {/* Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              className="font-body bg-primary hover:bg-primary-hover text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors duration-200"
              onClick={() => navigate("/")}
            >
              Find a car
            </button>
            <button
              className="font-semibold font-body px-5 py-2.5 rounded-lg text-sm text-white transition-colors duration-200"
              onClick={() => navigate("/support")}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.20)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.06)")
              }
            >
              Talk to us
            </button>
          </div>
        </div>
      </section>

      {/* ── 2. MISSION ──────────────────────────────────────────────────── */}
      <section className="w-full bg-white mt-10 mb-2">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            {/* Left — text */}
            <div className="flex-1">
              <p className="text-primary text-xs font-bold font-body tracking-widest uppercase mb-3">
                Our Mission
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-black leading-snug mb-6">
                Make every rental clear, fair, and fast.
              </h2>
              <p className="font-body text-gray-500 text-lg md:text-lg leading-relaxed mb-5">
                Rental pricing has always been opaque. Different suppliers,
                different rules, hidden add-ons. We started Rent Gurus to fix
                that — one search across the brands you already trust, with the
                small print made plain English.
              </p>
              <p className="font-body text-gray-500 text-lg md:text-lg leading-relaxed">
                We're a small team of travelers, engineers, and customer-support
                pros. We don't take kickbacks for ranking suppliers higher, and
                we never sell your data. Just the best deal for your trip —
                every time.
              </p>
            </div>

            {/* Right — 2×2 stats grid */}
            <div className="flex-1 grid grid-cols-2 gap-4 content-start mt-6">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <p className="text-3xl md:text-4xl font-extrabold font-heading text-primary mb-1">
                    {s.value}
                  </p>
                  <p className="font-body text-gray-500 text-sm">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. PRINCIPLES ───────────────────────────────────────────────── */}
      <section className="w-full bg-white mt-10 mb-2">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-16">
          <p className="text-primary text-xs font-bold font-body tracking-widest uppercase mb-3">
            What We Stand For
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-black mb-12">
            Four principles, every booking.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {principles.map((p) => (
              <div
                key={p.title}
                className="border border-gray-100 rounded-xl p-6 hover:shadow-md hover:border-primary hover:-translate-y-1 transition-all duration-200"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
                  {p.icon}
                </div>
                <h3 className="font-bold font-heading text-black text-lg mb-2">
                  {p.title}
                </h3>
                <p className="font-body text-gray-500 text-base leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS ─────────────────────────────────────────────── */}
      <section className="w-full bg-white mt-10 mb-20">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-16">
          <p className="text-primary text-xs font-bold font-body tracking-widest uppercase mb-3">
            How It Works
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-black mb-10">
            From search to steering wheel.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {steps.map((s) => (
              <div
                key={s.step}
                className="relative border border-gray-100 rounded-xl p-6 mt-4"
              >
                {/* Step badge — sitting on top border */}
                <div className="absolute -top-3.5 left-5 bg-primary text-white text-[10px] font-bold font-body tracking-widest uppercase px-2.5 py-1 rounded-full">
                  {s.step}
                </div>

                {/* Icon */}
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-5 mt-3">
                  {s.icon}
                </div>

                <h3 className="font-bold font-heading text-black text-lg mb-2">
                  {s.title}
                </h3>
                <p className="font-body text-gray-500 text-base leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
          <div
            className="max-w-7xl mx-auto px-6 rounded-2xl py-10 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            style={{
              background:
                "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
            }}
          >
            <div className="max-w-lg">
              <h2 className="text-2xl md:text-4xl font-extrabold font-heading text-white mb-2">
                Ready when you are.
              </h2>
              <p className="font-body text-white/70 text-lg leading-relaxed">
                Compare cars from every major rental brand in seconds. Free
                cancellation, no hidden fees.
              </p>
            </div>

            <button
              className="flex-shrink-0 bg-white hover:bg-gray-100 text-black font-semibold font-body px-6 py-3 rounded-xl text-sm flex items-center gap-2 border border-white/40 transition-colors duration-200"
              onClick={() => navigate("/")}
            >
              Start searching
              <span className="text-lg leading-none">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── 5. CTA BANNER ───────────────────────────────────────────────── */}
      {/* <section className="w-full py-12">
        <div
          className="max-w-7xl mx-auto px-6 rounded-2xl py-10 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          style={{
            background:
              "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
          }}
        >
          <div className="max-w-sm">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2">
              Ready when you are.
            </h2>
            <p className="text-white/70 text-sm leading-relaxed">
              Compare cars from every major rental brand in seconds. Free
              cancellation, no hidden fees.
            </p>
          </div>

          <button className="flex-shrink-0 bg-white hover:bg-gray-100 text-black font-semibold px-6 py-3 rounded-xl text-sm flex items-center gap-2 border border-white/40 transition-colors duration-200">
            Start searching
            <span className="text-lg leading-none">→</span>
          </button>
        </div>
      </section> */}
    </main>
  );
}
